/**
 * Lightweight signature image processing — no ML, no heavy downloads.
 *
 * Signatures are almost always dark ink on light paper. We convert them to
 * crisp black-and-transparent by:
 *   1. Detecting the dominant light background color from the image corners.
 *   2. Making near-background pixels transparent.
 *   3. Darkening the remaining (ink) pixels to solid black.
 *
 * Runs entirely on a 2D canvas — instant, zero dependencies.
 */

/**
 * Convert an uploaded image to a transparent PNG with black ink on a
 * transparent background. Suitable for signatures and stamps.
 */
export async function removeBackground(
  file: File,
): Promise<Blob> {
  const img = await loadImage(file);
  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return file;

  ctx.drawImage(img, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  const bg = sampleBackgroundColor(data, canvas.width, canvas.height);
  const bgThreshold = 30; // tolerance for "is this pixel background?"
  const inkThreshold = 180; // luminance below which we consider it "ink"

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

    // Is this pixel close to the background color? → make transparent.
    const isBackground =
      Math.abs(r - bg.r) < bgThreshold &&
      Math.abs(g - bg.g) < bgThreshold &&
      Math.abs(b - bg.b) < bgThreshold;

    if (isBackground || luminance > 230) {
      data[i + 3] = 0; // fully transparent
    } else if (luminance < inkThreshold) {
      // Dark ink pixel → solid black for crisp rendering.
      data[i] = 0x11;
      data[i + 1] = 0x18;
      data[i + 2] = 0x27; // gray-900
      data[i + 3] = 255;
    }
    // Mid-tone pixels (e.g. faded ink) pass through with their original alpha.
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve) => canvas.toBlob((blob) => resolve(blob ?? file), 'image/png'));
}

/** Sample the dominant background color from image corners and edges. */
function sampleBackgroundColor(
  data: Uint8ClampedArray,
  w: number,
  h: number,
): { r: number; g: number; b: number } {
  const points: [number, number][] = [];
  const step = Math.max(1, Math.floor(Math.min(w, h) / 20));
  // Top & bottom rows, left & right columns.
  for (let x = 0; x < w; x += step) {
    points.push([x, 0], [x, h - 1]);
  }
  for (let y = 0; y < h; y += step) {
    points.push([0, y], [w - 1, y]);
  }

  const samples: [number, number, number][] = [];
  for (const [x, y] of points) {
    const idx = (y * w + x) * 4;
    samples.push([data[idx], data[idx + 1], data[idx + 2]]);
  }

  // Average the light samples (likely background).
  const light = samples.filter(([r, g, b]) => r > 180 && g > 180 && b > 180);
  const source = light.length ? light : samples;
  const avg = source.reduce((acc, [r, g, b]) => ({
    r: acc.r + r, g: acc.g + g, b: acc.b + b,
  }), { r: 0, g: 0, b: 0 });
  const n = source.length;
  return { r: Math.round(avg.r / n), g: Math.round(avg.g / n), b: Math.round(avg.b / n) };
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

/** Convert a File or Blob to a base64 data URL. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}
